import { useEffect, useRef } from "react";

// Reuse the same CDN loading as BusMap so we only load Leaflet once.
const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS  = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

let leafletReady = false;
let leafletCallbacks = [];

function loadLeaflet(cb) {
  if (leafletReady) return cb();
  leafletCallbacks.push(cb);
  if (leafletCallbacks.length > 1) return;
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = LEAFLET_CSS;
  document.head.appendChild(link);
  const script = document.createElement("script");
  script.src = LEAFLET_JS;
  script.onload = () => {
    leafletReady = true;
    leafletCallbacks.forEach((fn) => fn());
    leafletCallbacks = [];
  };
  document.head.appendChild(script);
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatEta(km) {
  if (!Number.isFinite(km)) return "";
  const minutes = Math.max(1, Math.round(km / 0.28)); // ~17km/h avg
  if (minutes <= 1) return "Due";
  if (minutes < 60) return `~${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `~${h} hr ${m} min` : `~${h} hr`;
}

export default function VehicleDetailModal({ vehicle, userLocation, onClose }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  // Build the mini map: bus marker + blue-dot client + line between them.
  useEffect(() => {
    loadLeaflet(() => {
      const L = window.L;
      if (!L || !containerRef.current) return;

      const busLat = vehicle?.lat;
      const busLng = vehicle?.lng;

      // If no bus coords, fall back to center on user location.
      if (busLat === undefined || busLng === undefined) return;

      const map = L.map(containerRef.current, {
        center: [busLat, busLng],
        zoom: 14,
        zoomControl: true,
      });

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '© <a href="https://openstreetmap.org">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(map);

      // Bus marker
      const busIcon = L.divIcon({
        className: "",
        html: `<div style="
          width:30px;height:30px;
          background:#1D9E75;border:2.5px solid #fff;
          border-radius:8px;
          display:flex;align-items:center;justify-content:center;
          font-size:11px;font-weight:700;color:#fff;
          box-shadow:0 2px 8px rgba(0,0,0,0.5);
        ">${vehicle.route || "🚌"}</div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });
      const busMarker = L.marker([busLat, busLng], { icon: busIcon })
        .bindPopup(`<strong>Route ${vehicle.route || "—"}</strong><br/><span style="font-size:11px;">${vehicle.destination || ""}</span>`)
        .addTo(map);

      // User marker (blue dot) — only if we have user location
      let userMarker = null;
      let line = null;
      if (userLocation?.lat !== undefined && userLocation?.lng !== undefined) {
        const userIcon = L.divIcon({
          className: "",
          html: `<div style="
            width:16px;height:16px;
            background:#0074D9;border:3px solid #fff;
            border-radius:50%;
            box-shadow:0 0 0 4px rgba(0,116,217,0.25);
          "></div>`,
          iconSize: [16, 16],
          iconAnchor: [8, 8],
        });
        userMarker = L.marker([userLocation.lat, userLocation.lng], { icon: userIcon })
          .bindPopup("You are here")
          .addTo(map);

        line = L.polyline([[busLat, busLng], [userLocation.lat, userLocation.lng]], {
          color: "#0074D9",
          weight: 2,
          dashArray: "5,5",
          opacity: 0.7,
        }).addTo(map);

        // Fit bounds to show both
        map.fitBounds(L.latLngBounds([[busLat, busLng], [userLocation.lat, userLocation.lng]]), { padding: [40, 40] });
      }

      mapRef.current = map;
      return () => { map.remove(); mapRef.current = null; };
    });
  }, [vehicle, userLocation]);

  const km = (vehicle?.lat !== undefined && userLocation?.lat !== undefined && vehicle?.lng !== undefined && userLocation?.lng !== undefined)
    ? haversineKm(userLocation.lat, userLocation.lng, vehicle.lat, vehicle.lng)
    : null;

  const etaLabel = km !== null ? formatEta(km) : (vehicle?.etaLabel || "Scheduled");

  return (
    <div className="vehicle-modal-overlay" onClick={onClose}>
      <div className="vehicle-modal" onClick={(e) => e.stopPropagation()}>
        <div className="vehicle-modal-header">
          <span className="route-badge" style={{ background: "#1D9E75" }}>{vehicle.route || "🚌"}</span>
          <div className="vehicle-modal-title">
            <strong>Route {vehicle.route || "Unknown"}</strong>
            <span>{vehicle.destination || ""}</span>
          </div>
          <button className="vehicle-modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div
          ref={containerRef}
          style={{ width: "100%", height: "260px", borderRadius: "10px", overflow: "hidden", border: "0.5px solid #e2e8f0" }}
        />

        <div className="vehicle-modal-info">
          {userLocation?.lat !== undefined ? (
            <>
              <div className="eta-row">
                <span className="eta-label">ETA to you</span>
                <span className="eta-value">{etaLabel}</span>
              </div>
              {km !== null && (
                <div className="eta-distance">
                  {km < 1 ? `${Math.round(km * 1000)}m` : `${km.toFixed(1)}km`} straight-line{vehicle.vehicleRef ? ` · Vehicle ${vehicle.vehicleRef}` : ""}
                </div>
              )}
            </>
          ) : (
            <div className="eta-hint">
              Enable location to see ETA to you and your position on the map.
            </div>
          )}
          {vehicle.recordedAt && (
            <div className="eta-recorded">Reported {new Date(vehicle.recordedAt).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</div>
          )}
        </div>
      </div>
    </div>
  );
}
